type StatusTrendCounts = { passed: number; failed: number; skipped: number };

type ChartRow = {
  label: string;
  passed: number;
  failed: number;
  skipped: number;
};

type SeriesRow = {
  label: string;
  passRate?: number;
  frequency?: number;
  duration?: number;
};

const formatChartLabel = (value: Date): string =>
  value.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: '2-digit' });

const makeSeededRandom = (seed: number): (() => number) => {
  let state = seed;
  return () => {
    state += 0x6d2b79f5;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const buildRandomSeries = (
  count: number,
  min: number,
  max: number,
  decimals = 0,
  seed = 1,
): number[] => {
  const rand = makeSeededRandom(seed);
  const scale = 10 ** decimals;
  return Array.from({ length: count }, () => {
    const value = min + rand() * (max - min);
    return Math.round(value * scale) / scale;
  });
};

type LandingChartData = {
  statusTrendChartData: ChartRow[];
  passRateChartData: SeriesRow[];
  frequencyChartData: SeriesRow[];
  durationChartData: SeriesRow[];
};

export const buildLandingChartData = (statusTrendSeries: StatusTrendCounts[]): LandingChartData => {
  const statusTrendRows = statusTrendSeries.map((counts, index) => ({
    id: `status-trend-${index}`,
    date: new Date(Date.now() - (statusTrendSeries.length - 1 - index) * 24 * 60 * 60 * 1000),
    counts,
  }));
  const statusTrendChartData: ChartRow[] = statusTrendRows.map((row) => ({
    label: formatChartLabel(row.date),
    passed: row.counts.passed,
    failed: row.counts.failed,
    skipped: row.counts.skipped,
  }));
  const chartCount = statusTrendChartData.length;
  const passRateSeriesValues = statusTrendSeries.map((counts) => {
    const totalExecutions = counts.passed + counts.failed + counts.skipped;
    if (totalExecutions <= 0) return 0;
    return Number(((counts.passed / totalExecutions) * 100).toFixed(2));
  });
  const frequencySeriesValues = buildRandomSeries(chartCount, 0.3, 7.5, 1, 29);
  const durationSeriesValues = buildRandomSeries(chartCount, 18, 82, 0, 41);
  const passRateChartData: SeriesRow[] = statusTrendChartData.map((row, index) => ({
    label: row.label,
    passRate: passRateSeriesValues[index] ?? 0,
  }));
  const frequencyChartData: SeriesRow[] = statusTrendChartData.map((row, index) => ({
    label: row.label,
    frequency: frequencySeriesValues[index] ?? 0,
  }));
  const durationChartData: SeriesRow[] = statusTrendChartData.map((row, index) => ({
    label: row.label,
    duration: durationSeriesValues[index] ?? 0,
  }));
  return {
    statusTrendChartData,
    passRateChartData,
    frequencyChartData,
    durationChartData,
  };
};
